/** @jsx jsx */
import { Button } from "@eworkit/iris";
import { useBreakpointIndex } from "@theme-ui/match-media";
import React, { useContext } from "react";
import { FC } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { DialogDisclosure } from "reakit/Dialog";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { IJobApplication } from "@/data/job-applications";
import { ExitModalActionType } from "@/data/job-applications/types/ExitModalActionType";
import { IJobRequest } from "@/data/job-requests";
import { ApplyModal } from "@/views/job-applications/apply/ApplyModal";
import { useApplyModalState } from "@/views/job-applications/apply/useApplyModalState";
import { router } from "@/views/router";

interface JobRequestApplyButtonProps {
  jobRequest: IJobRequest;
  drafts?: IJobApplication[];
  onApplyModalClosed?: (action?: ExitModalActionType) => void;
}

export const JobRequestApplyButton: FC<JobRequestApplyButtonProps> = ({ jobRequest, drafts, onApplyModalClosed }) => {
  const { t } = useTranslation("session");
  const { contextCompany, contextConsultantProfileId } = useContext(UserContext);

  const breakpoint = useBreakpointIndex();

  const applyModalState = useApplyModalState(jobRequest);

  const button = (
    <Button
      as={DialogDisclosure}
      {...applyModalState}
      size="lg"
      sx={{
        minWidth: [null, null, 170],
        width: ["100%", null, "unset"],
      }}>
      {t("JOB_REQUESTS.DETAIL.BUTTON_APPLY")}
    </Button>
  );

  const publicButton = (
    <Button
      as={Link}
      to={router.get("auth", {}, { tab: "login", jobRequestId: jobRequest.id }).path}
      size="lg"
      sx={{
        minWidth: [null, null, 170],
        width: ["100%", null, "unset"],
      }}>
      {t("JOB_REQUESTS.DETAIL.BUTTON_APPLY")}
    </Button>
  );

  return (
    <React.Fragment>
      {breakpoint <= 1
        ? createPortal(
            <div
              sx={{
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: "n0",
                p: "sm",
                zIndex: 1,
              }}>
              {contextConsultantProfileId ? button : publicButton}
            </div>,
            document.body,
          )
        : contextConsultantProfileId
        ? button
        : publicButton}

      {applyModalState.visible && (
        <ApplyModal
          dialogState={applyModalState}
          jobRequest={jobRequest}
          initialDraft={!contextCompany && drafts?.[0] ? drafts[0] : undefined}
          onClosed={onApplyModalClosed}
        />
      )}
    </React.Fragment>
  );
};
